// Plans: Free and Pro (see plans.js on the server). Every new member tries every feature free for
// 14 days; after that the core journal stays free and the Pro features need a plan. Until the
// paywall is switched on (PAYWALL=1) this is an admin-only preview.
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from './api';
import { confirmDialog } from './confirmDialog';
import { useI18n } from './i18n';
import { Icon } from './Icons';
import { COMING_LATER, FREE_FEATURES, PRICES, PRO_FEATURES, REFUND_DAYS } from './planData';


export const money = (n) => `$${Number.isInteger(n) ? n : n.toFixed(2)}`;

// Whether this member's Pro features are closed right now.
export const isLocked = (user) => Boolean(user && user.plan && user.plan.locked);

// "Keep using X with Pro": shown in place of a Pro page, or as a small line for a Pro part of a page.
export function ProLock({ title, text, compact }) {
  const { t } = useI18n();
  if (compact) {
    return (
      <p className="pro-lock-line">
        <Icon name="lock" /> {text || t('This is a Pro feature.')} <Link to="/app/plans">{t('See plans')} &rarr;</Link>
      </p>
    );
  }
  return (
    <section className="pro-lock">
      <span className="pro-lock-icon"><Icon name="lock" /></span>
      <h2>{title || t('This is a Pro feature')}</h2>
      <p>{text || t('Your free trial of every feature has ended. Your trades and notes are all still here, and the core journal stays free.')}</p>
      <Link to="/app/plans" className="cta-button">{t('See plans')} &rarr;</Link>
    </section>
  );
}

// "11 days left in your free trial": above every app page while the trial runs (once the paywall is on).
export function TrialBanner({ user }) {
  const { t, lang } = useI18n();
  const p = user && user.plan;
  if (!p || user.isDemo || !(p.paywall || p.preview)) return null;
  if (p.plan === 'trial' && p.grace) {
    const until = new Date(p.trialEndsAt).toLocaleDateString(lang === 'es' ? 'es-MX' : 'en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' });
    return (
      <div className="trial-banner">
        <span>{t('Pro is here. You keep every feature until {date}.', { date: until })}{p.founding && ` ★ ${t('As a founding member, you get 50% off Pro for life.')}`}</span>
        <Link to="/app/plans">{t('See plans')} &rarr;</Link>
      </div>
    );
  }
  if (p.plan === 'trial') {
    return (
      <div className="trial-banner">
        <span>{p.trialDaysLeft === 1 ? t('Last day of your free trial of every feature.') : t('{n} days left in your free trial of every feature.', { n: p.trialDaysLeft })}</span>
        <Link to="/app/plans">{t('See plans')} &rarr;</Link>
      </div>
    );
  }
  return null;
}

// Opens when the server turns down a Pro request (api.js sends 'mm-pro-gate').
export function ProGate() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [feature, setFeature] = useState(null);
  useEffect(() => {
    const show = (e) => setFeature((e.detail && e.detail.feature) || 'pro');
    window.addEventListener('mm-pro-gate', show);
    return () => window.removeEventListener('mm-pro-gate', show);
  }, []);
  useEffect(() => {
    if (!feature) return undefined;
    const onKey = (e) => e.key === 'Escape' && setFeature(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [feature]);
  if (!feature) return null;
  const text = {
    sync: t('Live broker sync is part of Pro. You can still import your trades from a file on the Free plan.'),
    accounts: t('The Free plan includes 1 account. You keep every account you already have; adding more is part of Pro.'),
    charts: t('Trade charts are part of Pro.'),
    notebook: t('New day pages, templates, folders and weekly reviews are part of Pro. You can still read everything you wrote and keep writing notes.'),
  }[feature] || t('This is part of Pro.');
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && setFeature(null)}>
      <div className="modal pro-gate" role="dialog" aria-modal="true" aria-labelledby="pro-gate-title">
        <div className="modal-header">
          <h2 id="pro-gate-title">{t('This is a Pro feature')}</h2>
          <button type="button" className="modal-close" onClick={() => setFeature(null)} aria-label={t('Close')}>&times;</button>
        </div>
        <p>{text}</p>
        <div className="modal-actions">
          <button type="button" onClick={() => { setFeature(null); navigate('/app/plans'); }}>{t('See plans')} &rarr;</button>
          <button type="button" className="secondary" onClick={() => setFeature(null)}>{t('Not now')}</button>
        </div>
      </div>
    </div>
  );
}

// Paddle's checkout script (paddle.com), loaded once when the Plans page can take payments.
let paddleReady = null;
function loadPaddle(config, onEvent) {
  if (!paddleReady) {
    paddleReady = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
      script.onload = () => {
        if (config.environment === 'sandbox') window.Paddle.Environment.set('sandbox');
        window.Paddle.Initialize({ token: config.clientToken, eventCallback: (e) => paddleEvents.forEach((fn) => fn(e)) });
        resolve(window.Paddle);
      };
      script.onerror = () => { paddleReady = null; reject(new Error('Could not load the checkout. Check your connection and try again.')); };
      document.head.appendChild(script);
    });
  }
  paddleEvents.add(onEvent);
  return paddleReady;
}
const paddleEvents = new Set();

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

function PlanStatus({ p }) {
  const { t, lang } = useI18n();
  const date = (d) => new Date(d).toLocaleDateString(lang === 'es' ? 'es-MX' : 'en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  if (p.subscribed && p.pastDue) return <p className="plans-status warn">{t('Your last Pro payment didn’t go through. Paddle will try again; update your payment method below to keep Pro.')}</p>;
  if (p.subscribed && p.endsAt) return <p className="plans-status">{t('Your Pro plan is cancelled. You keep every Pro feature until {date}, then your account moves to the Free plan.', { date: date(p.endsAt) })}</p>;
  if (p.plan === 'pro' && p.renewsAt) return <p className="plans-status">{t('You’re on Pro. It renews on {date}.', { date: date(p.renewsAt) })}</p>;
  if (p.plan === 'pro') return <p className="plans-status">{t('You have every Pro feature.')}</p>;
  if (p.plan === 'trial') return <p className="plans-status">{t('Your free trial of every feature runs until {date}. After that you keep the Free plan unless you choose Pro.', { date: date(p.trialEndsAt) })}</p>;
  return <p className="plans-status">{t('You’re on the Free plan. Your trades, notes and everything you wrote are all still here.')}</p>;
}

export default function Plans({ user, onUserChange, onSessionExpired }) {
  const { t, lang } = useI18n();
  const p = (user && user.plan) || { prices: PRICES, freeAccounts: 3 };
  const [interval, setInterval_] = useState('year');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const isPreview = !p.paywall;
  const half = p.founding ? 0.5 : 1;
  const perMonth = interval === 'year' ? (p.prices.year * half) / 12 : p.prices.month * half;
  const [billing, setBilling] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => { api('/billing/config').then(setBilling).catch(() => setBilling({ enabled: false })); }, []);

  const fail = (err) => (err.status === 401 ? onSessionExpired() : setMessage(err.message));

  // After a checkout, Paddle tells our server about the new subscription; ask until it's in.
  async function waitForPro() {
    setChecking(true);
    try {
      for (let i = 0; i < 12; i += 1) {
        const fresh = await api('/billing/refresh', { method: 'POST' }).catch(() => null);
        if (fresh && fresh.plan && fresh.plan.subscribed) { onUserChange(fresh); return; }
        await wait(2500);
      }
      setMessage(t('Your payment went through. Your plan can take a minute to update: refresh this page shortly.'));
    } finally {
      setChecking(false);
    }
  }

  async function choosePro() {
    setBusy(true);
    setMessage('');
    try {
      const Paddle = await loadPaddle(billing, (e) => {
        if (e && e.name === 'checkout.error') setMessage(`${t('The checkout couldn’t open.')} ${(e.detail || (e.error && (e.error.detail || e.error.message))) || ''}`.trim());
        if (e && e.name === 'checkout.completed') {
          setTimeout(() => window.Paddle && window.Paddle.Checkout.close(), 1500);
          waitForPro();
        }
      });
      const { transactionId } = await api('/billing/checkout', { method: 'POST', body: { interval } });
      Paddle.Checkout.open({ transactionId, settings: { displayMode: 'overlay', theme: 'dark', locale: lang === 'es' ? 'es' : 'en', allowLogout: false } });
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  async function cancelPro() {
    const ok = await confirmDialog({
      title: t('Cancel Pro?'),
      message: p.renewsAt
        ? t('You keep every Pro feature until {date}. After that your account moves to the Free plan, and your trades and notes stay.', { date: new Date(p.renewsAt).toLocaleDateString(lang === 'es' ? 'es-MX' : 'en-US', { month: 'long', day: 'numeric', year: 'numeric' }) })
        : t('Your account moves to the Free plan at the end of the period you paid for. Your trades and notes stay.'),
      confirmLabel: t('Cancel Pro'), cancelLabel: t('Keep Pro'), danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try { onUserChange(await api('/billing/cancel', { method: 'POST' })); } catch (err) { fail(err); } finally { setBusy(false); }
  }

  async function resumePro() {
    setBusy(true);
    try { onUserChange(await api('/billing/resume', { method: 'POST' })); } catch (err) { fail(err); } finally { setBusy(false); }
  }

  async function openPortal() {
    setBusy(true);
    try {
      const { url, paymentMethodUrl } = await api('/billing/portal', { method: 'POST' });
      const to = (p.pastDue && paymentMethodUrl) || url;
      if (to) window.open(to, '_blank', 'noopener');
    } catch (err) { fail(err); } finally { setBusy(false); }
  }

  async function togglePreview(on) {
    setBusy(true);
    try {
      onUserChange(await api('/auth/preview-free', { method: 'PUT', body: { on } }));
    } catch (err) {
      if (err.status === 401) onSessionExpired(); else setMessage(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="plans-page">
      <header className="plans-head">
        <h1>{t('Plans')} {isPreview && <span className="admin-preview-badge">{t('Admin only')}</span>}</h1>
        <p className="lead">{t('Every new member gets 14 days of every feature, free. No credit card. After that the core journal stays free, and Pro adds the rest.')}</p>
        <PlanStatus p={p} />
      </header>

      <div className="plans-interval" role="group" aria-label={t('Billing')}>
        <button type="button" className={`secondary small${interval === 'month' ? ' active' : ''}`} aria-pressed={interval === 'month'} onClick={() => setInterval_('month')}>{t('Monthly')}</button>
        <button type="button" className={`secondary small${interval === 'year' ? ' active' : ''}`} aria-pressed={interval === 'year'} onClick={() => setInterval_('year')}>
          {t('Yearly')} <span className="plans-save">{t('Save {n}%', { n: Math.round((1 - p.prices.year / (p.prices.month * 12)) * 100) })}</span>
        </button>
      </div>

      <div className="plans-grid">
        <section className={`plan-card${p.plan === 'free' ? ' current' : ''}`}>
          <h2>{t('Free')}</h2>
          <p className="plan-price"><strong>$0</strong></p>
          <p className="field-hint">{t('The core journal, free for good.')}</p>
          <ul>{FREE_FEATURES.map((f) => <li key={f}>{t(f, { n: p.freeAccounts })}</li>)}</ul>
          {p.plan === 'free' && <span className="plan-current">{t('Your plan')}</span>}
        </section>

        <section className={`plan-card pro${p.plan === 'pro' ? ' current' : ''}`}>
          <h2><Icon name="star" /> {t('Pro')}</h2>
          <p className="plan-price">
            <strong>{money(perMonth)}</strong> <span>{t('a month')}</span>
            {half < 1 && <s className="plan-was">{money(interval === 'year' ? p.prices.year / 12 : p.prices.month)}</s>}
          </p>
          <p className="field-hint">
            {interval === 'year' ? t('{price} billed once a year', { price: money(p.prices.year * half) }) : t('Billed monthly. Cancel any time.')}
            {half < 1 && <> · <strong className="plan-founding">{t('Founding member: 50% off any plan')}</strong></>}
          </p>
          <ul>{PRO_FEATURES.map((f) => <li key={f}>{t(f)}</li>)}</ul>
          {COMING_LATER && <p className="field-hint plan-later">{t(COMING_LATER)}</p>}
          {p.subscribed ? <span className="plan-current">{t('Your plan')}</span> : (
            <>
              <button type="button" disabled={!billing || !billing.enabled || busy || checking} onClick={choosePro}>
                {checking ? t('Confirming your payment…') : busy ? t('Opening the checkout…') : t('Choose Pro')}
              </button>
              {billing && !billing.enabled && <p className="field-hint">{t('Payments open soon. Nothing is charged today.')}</p>}
              {billing && billing.enabled && billing.environment === 'sandbox' && <p className="field-hint admin-note">Test mode (Paddle sandbox): use card 4242 4242 4242 4242, any future date, CVC 100.</p>}
              {message && <p className="form-error">{message}</p>}
              <p className="field-hint">{t('{n}-day money-back guarantee on your first payment.', { n: REFUND_DAYS })}</p>
            </>
          )}
        </section>
      </div>

      {p.subscribed && (
        <section className="plans-manage">
          <h2>{t('Your subscription')}</h2>
          <p className="field-hint">
            {p.interval === 'year' ? t('Pro, billed yearly.') : t('Pro, billed monthly.')}{' '}
            {t('Payments are handled by Paddle.com, our reseller.')}
          </p>
          <div className="plans-manage-actions">
            <button type="button" className="secondary small" disabled={busy} onClick={openPortal}>{t('Payment method and receipts')}</button>
            {p.endsAt
              ? <button type="button" className="small" disabled={busy} onClick={resumePro}>{t('Keep Pro')}</button>
              : <button type="button" className="link-button danger-link" disabled={busy} onClick={cancelPro}>{t('Cancel Pro')}</button>}
          </div>
        </section>
      )}
      {message && p.subscribed && <p className="form-error">{message}</p>}

      {user && user.isAdmin && (
        <section className="plans-admin admin-preview-box">
          <h2>{t('Preview as a Free member')}</h2>
          <p className="field-hint">
            {p.paywall ? 'The paywall is ON for members.' : 'The paywall is OFF: members keep every feature until PAYWALL=1 is set on Render.'}
            {' '}Turn this on to see the site as a member whose trial has ended.
          </p>
          <label className="settings-switch">
            <input type="checkbox" checked={Boolean(p.preview)} disabled={busy} onChange={(e) => togglePreview(e.target.checked)} />
            <span><strong>See the site as a Free member</strong></span>
          </label>
        </section>
      )}
    </div>
  );
}
